import React from 'react';
import { useCompare, MAX_COMPARE_LIMIT } from '../context/CompareContext';
import { useLanguage } from '../context/LanguageContext';
import { Scale, X, ArrowRight, Trash2 } from 'lucide-react';

export const CompareFloatingBar: React.FC = () => {
  const { comparedProperties, removeFromCompare, clearCompare, openCompareModal } = useCompare();
  const { language } = useLanguage();
  const isFr = language === 'fr';

  if (comparedProperties.length === 0) {
    return null;
  }

  return (
    <aside
      aria-label={isFr ? 'Barre de comparaison de propriétés' : 'Property comparison drawer'}
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[95%] max-w-4xl transition-all duration-300 animate-in fade-in slide-in-from-bottom-6"
    >
      <div className="bg-slate-950/95 backdrop-blur-md text-white border border-slate-800 rounded-3xl p-3 sm:p-3.5 shadow-2xl flex flex-col sm:flex-row items-center justify-between gap-3">
        {/* Left: Indicator & Thumbnails */}
        <div className="flex items-center gap-3 w-full sm:w-auto overflow-x-auto no-scrollbar py-0.5">
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-9 h-9 rounded-2xl bg-amber-400 text-slate-950 flex items-center justify-center font-black shadow-md">
              <Scale className="w-5 h-5" />
            </div>
            <div className="hidden md:block">
              <div className="text-xs font-bold text-white flex items-center gap-1.5">
                <span>{isFr ? 'Comparateur' : 'Compare'}</span>
                <span className="text-[10px] bg-slate-800 text-amber-300 font-extrabold px-1.5 py-0.2 rounded-full border border-slate-700">
                  {comparedProperties.length}/{MAX_COMPARE_LIMIT}
                </span>
              </div>
              <p className="text-[10px] text-slate-400">
                {isFr ? 'Titres fonciers & prix au m²' : 'Land titles & price/m²'}
              </p>
            </div>
          </div>

          {/* Property Thumbnails Strip */}
          <div className="flex items-center gap-2 shrink-0">
            {comparedProperties.map((prop) => (
              <div
                key={prop.id}
                className="relative group flex items-center gap-1.5 bg-slate-900 border border-slate-800 rounded-xl p-1 pr-2 max-w-[140px] sm:max-w-[160px]"
              >
                <img
                  src={prop.image}
                  alt={prop.title}
                  className="w-8 h-8 rounded-lg object-cover shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-[10.5px] font-bold text-slate-200 truncate">
                    {prop.title}
                  </div>
                  <div className="text-[9.5px] font-extrabold text-amber-400 truncate">
                    {prop.price}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFromCompare(prop.id)}
                  className="w-4 h-4 rounded-full bg-slate-800 hover:bg-red-950 hover:text-red-300 text-slate-400 flex items-center justify-center transition-colors cursor-pointer shrink-0"
                  title={isFr ? 'Retirer' : 'Remove'}
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2 w-full sm:w-auto justify-end shrink-0">
          <button
            type="button"
            onClick={clearCompare}
            className="px-2.5 py-2 text-xs font-semibold text-slate-400 hover:text-white transition-colors flex items-center gap-1 cursor-pointer"
            title={isFr ? 'Vider la sélection' : 'Clear all'}
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{isFr ? 'Vider' : 'Clear'}</span>
          </button>

          <button
            type="button"
            onClick={openCompareModal}
            className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-2xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-black text-xs sm:text-sm shadow-lg shadow-amber-400/20 transition-all cursor-pointer transform hover:scale-102 active:scale-100"
          >
            <span>
              {isFr
                ? `Comparer (${comparedProperties.length})`
                : `Compare (${comparedProperties.length})`}
            </span>
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
};
